import { useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Button, Modal, Popconfirm } from 'antd';
import styled from 'styled-components';
import { useAppDispatch } from '../../methods/redux/hooks';
import NativeNumber from '../../component/Numeral';
import { commas } from '../../methods/utils/general-utils';
import {
	refundStrangerDeposit,
	allowStrangerDeposit,
} from '../../methods/redux/reducers/transactions';

interface IInCreditItem {
	data: any;
}

const Style = styled.div`
	background-color: #f8f8f8;
	padding: 20px;
	margin-bottom: 15px;

	& .amount {
		display: flex;
		font-weight: bold;
		font-size: 16px;
		& img {
			margin-right: 7px;
			width: 20px;
			height: 20px;
		}
	}

	& .details {
		border-top: 1px dashed #ccc;
		margin-top: 12px;
		padding: 15px 0;

		& > * {
			display: inline-block;
			min-width: 200px;
			margin-bottom: 7px;
			padding: 5px;
		}

		& .label {
			font-size: 13px;
			color: #808080;
			font-style: italic;
		}
	}
`;

const ModalContent = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 0.5em;
`;

const InCreditItem = ({ data }: IInCreditItem) => {
	const dispatch = useAppDispatch();
	const [visible, setVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleRefund = async () => {
		try {
			setLoading(true);
			await dispatch(refundStrangerDeposit({ id: data?._id }));
			setLoading(false);
		} catch (error) {
			setLoading(false);
		}
	};

	const handleAllowDeposit = async () => {
		try {
			setLoading(true);
			await dispatch(allowStrangerDeposit({ id: data?._id }));
			setLoading(false);
		} catch (error) {
			setLoading(false);
		}
	};

	return (
		<Style>
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<div className="amount">
					<img alt="" src={data.currencyId?.iconUrl} />
					<p>
						<NativeNumber value={data.transactionAmount} />{' '}
						{data.currencyId?.symbol}
					</p>
				</div>

				<Button
					type="primary"
					size="small"
					disabled={!data?.strangerDeposit}
					onClick={() => setVisible(true)}
				>
					Refund Stranger Deposit
				</Button>
			</div>
			<div className="details">
				<div>
					<p>
						<Link title="View Profile" to={`/user-profile/${data.userId?._id}`}>
							<p>@{data.userId?.username}</p>
						</Link>
					</p>
					<p className="label">Customer</p>
				</div>
				<div>
					<p>
						{data.transactionAmountInUSD
							? commas(data.transactionAmountInUSD)
							: commas(
									Number(data.transactionAmount) *
										Number(data.currencyId?.usdValue || 0)
							  )}
					</p>
					<p className="label">Amount in USD</p>
				</div>
				<div>
					<p>{data._id}</p>
					<p className="label">Id</p>
				</div>
				<div>
					<p>{data.blockchainId?.abbreviation}</p>
					<p className="label">Blockchain Network</p>
				</div>
				<div>
					{data.blockchainId?.explorerUrl ? (
						<a
							href={`https://${data.blockchainId?.explorerUrl}/tx/${data.transactionHash}`}
							target="_blank"
							rel="noreferrer"
						>
							Hash
						</a>
					) : (
						'--'
					)}
					<p className="label">Transaction Hash</p>
				</div>
				<div>
					<p>{data.transactionSource}</p>
					<p className="label">Source</p>
				</div>
				<div>
					<p>{data.title}</p>
					<p className="label">Description</p>
				</div>
				<div>
					<p>{data.blockchainValidation}</p>
					<p className="label">Blockchain Validation</p>
				</div>
				<div>
					<p>{data.providerKey}</p>
					<p className="label">Provider Key</p>
				</div>
				<div>
					<p>{data.fundsRemitted ? 'Remitted' : 'Not Yet Remitted'}</p>
					<p className="label">
						{data.transactionSource === 'bank'
							? 'Funds Credited to Custodial Wallet'
							: 'Funds Remitted to Custodial Wallet'}
					</p>
				</div>
				{data?.fiatDepositDetail || data?.bankDetails ? (
					<div>
						<p>
							{data?.fiatDepositDetail?.sessionid ??
								data?.bankDetails?.sessionId ??
								'--'}
						</p>
						<p className="label">Session Id</p>
					</div>
				) : null}
				{data?.fiatDepositDetail || data?.bankDetails ? (
					<div>
						<p>
							{data?.fiatDepositDetail?.sourceAccountNumber ??
								data?.bankDetails?.originatoraccountnumber ??
								data?.bankDetails?.sourceAccountNumber ??
								'--'}
						</p>
						<p className="label">Fund Source Account Number</p>
					</div>
				) : null}
				{data?.fiatDepositDetail || data?.bankDetails ? (
					<div>
						<p>
							{data?.fiatDepositDetail?.sourceAccountName ??
								data?.bankDetails?.originatorname ??
								data?.bankDetails?.sourceAccountName ??
								'--'}
						</p>
						<p className="label">Fund Source Name</p>
					</div>
				) : null}
				{data.fiatDepositDetail || data?.bankDetails ? (
					<div>
						<p>
							{data.fiatDepositDetail?.transactionReference ??
								data?.bankDetails?.reference ??
								'--'}
						</p>
						<p className="label">Reference</p>
					</div>
				) : null}
				{data?.bankDetails && (
					<div>
						<p>
							{data?.bankDetails?.settlementStatus
								? data?.bankDetails?.settlementStatus
								: '--'}
						</p>
						<p className="label">Settlement Status</p>
					</div>
				)}
				<div>
					{data?.kycId ? (
						<p>
							{data?.kycId?.firstName} {data?.kycId?.middleName}{' '}
							{data?.kycId?.lastName}
						</p>
					) : (
						'--'
					)}
					<p className="label">KYC Names</p>
				</div>
				<div>
					<p>{data.status}</p>
					<p className="label">Status</p>
				</div>
				<div>
					<p>{data.cronTouch ? 'True' : 'False'}</p>
					<p className="label">Cron Touch</p>
				</div>
				<div>
					<p>
						<NativeNumber value={data.settlementAmount} decimalPlaces={4} />
					</p>
					<p className="label">Settlement Amount</p>
				</div>
				<div>
					<p>
						{Number(data?.transactionAmount) - Number(data?.settlementAmount)}
					</p>
					<p className="label">Deposit Fee</p>
				</div>
				<div>
					<p>{data?.robotComment}</p>
					<p className="label">Robot Comment</p>
				</div>
				<div>
					<p>
						{data.createdAt
							? moment(data.createdAt).format('MMM DD, YY / hh:mm:ss a')
							: '--'}
					</p>
					<p className="label">Date Created</p>
				</div>
				<div>
					<p>
						{data.updatedAt
							? moment(data.updatedAt).format('MMM DD, YY / hh:mm:ss a')
							: '--'}
					</p>
					<p className="label">Last Updated</p>
				</div>
				<div>
					<p>{data.lastUpdatedByUserForm}</p>
					<p className="label">User Form (Last Updater)</p>
				</div>
				<div>
					<p>{data?.allowStrangerDeposit ? 'True' : 'False'}</p>
					<p className="label">Allow Stranger Deposit</p>
				</div>
				<div>
					<p>{data?.strangerDepositRefunded ? 'True' : 'False'}</p>
					<p className="label">Refunded</p>
				</div>
				<div>
					<p>
						{data?.fiatDepositDetail?.refundSessionId ||
							data?.refundTransactionDetails?.sessionId}
					</p>
					<p className="label">Refund Session Id</p>
				</div>
				<div>
					<p>
						{data?.fiatDepositDetail?.sourceBankName ??
							data?.bankDetails?.sourceBankName ??
							'--'}
					</p>
					<p className="label">Bank Name</p>
				</div>
			</div>

			<Modal
				title="Refund Stranger Deposit"
				width="50%"
				visible={visible}
				footer={[
					<div
						style={{
							display: 'flex',
							gap: '6px',
							alignItems: 'center',
							justifyContent: 'right',
						}}
					>
						<Popconfirm
							title="Are you sure you want to allow stranger deposit?"
							onConfirm={handleAllowDeposit}
						>
							<Button loading={loading} type="default">
								Allow Deposit
							</Button>
						</Popconfirm>
						<Popconfirm
							title="Are you sure you want to refund?"
							onConfirm={handleRefund}
						>
							{data?.strangerDepositRefunded &&
							data?.refundTransactionDetails?.sessionId ? (
								<p style={{ fontWeight: 'bold', color: 'red' }}>REFUNDED</p>
							) : (
								<Button
									loading={loading}
									type="primary"
									disabled={
										data?.allowStrangerDeposit ||
										(data?.strangerDepositRefunded &&
											data?.refundTransactionDetails?.sessionId)
									}
								>
									Refund
								</Button>
							)}
						</Popconfirm>
					</div>,
				]}
				onCancel={() => setVisible(false)}
			>
				<ModalContent>
					<p>The funds will be returned to the details below:</p>
					<p>
						Account number:{' '}
						<strong>
							{data?.bankDetails ? (
								<span>
									{data?.fiatDepositDetail?.sourceAccountNumber ??
										data?.bankDetails?.originatoraccountnumber ??
										data?.bankDetails?.sourceAccountNumber ??
										'--'}
								</span>
							) : (
								'--'
							)}
						</strong>
					</p>
					<p>
						Account name:{' '}
						<strong>
							{data?.bankDetails ? (
								<span>
									{data?.fiatDepositDetail?.sourceAccountName ??
										data?.bankDetails?.originatorname ??
										data?.bankDetails?.sourceAccountName ??
										'--'}
								</span>
							) : (
								'--'
							)}
						</strong>
					</p>
					<p>
						Bank:{' '}
						<strong>
							{data?.fiatDepositDetail?.sourceBankName ??
								data?.bankDetails?.sourceBankName ??
								'--'}
						</strong>
					</p>
				</ModalContent>
			</Modal>
		</Style>
	);
};

export default InCreditItem;
